import React, { useState, useEffect } from 'react';
import {
  useLocation,
  useHistory,
} from 'react-router-dom/cjs/react-router-dom.min';
import { MainContainer, Button, CloseImg, CountBadge, IconImg } from './style';
import { useQuery } from '@tanstack/react-query';
import {
  getCommunityTasks,
  getTasks,
  getAssignments,
  getCompletedTasks,
  updateTasksViewedCount,
  updateMarkingToolsViewedCount,
  updateSuccessCriteriasViewedCount,
  updateShortAnswersViewedCount
} from '../../service';
import commentSelected from '../../static/img/commentSelected.svg';
import commentUnSelected from '../../static/img/commentUnSelected.svg';
import markSelected from '../../static/img/markSelected.svg';
import markUnSelected from '../../static/img/markUnSelected.svg';
import closeicon from '../../static/img/closecircle.svg';
import shareIcon from '../../static/img/24grayshare.svg';
import shareIconSelected from '../../static/icons/purple-share-icon.svg';
import markingTemplateSelected from '../../static/img/rubric-outcome.svg';
import markingTemplateUnSelected from '../../static/icons/black-rubric-icon.svg';
import shortAnswerIcon from '../../static/icons/ShortAnsIcon.svg';
import purpleShortAnsIcon from '../../static/icons/PurpleShortAns.svg';
import advancedControlsIcon from '../../static/icons/advanced-controls.svg';
import advancedControlsActiveIcon from '../../static/icons/advanced-controls-active.svg';

import { getUserRole } from '../../userLocalDetails';
import { isActiveButton, isTeacherWithoutClass } from './rules';
import { isTabletView } from '../ReactiveRender';
import { useAssignmentsAll, useClassData, useTasks, useProfile, useShares } from '../state/hooks';
import { useLexicon } from '../../hooks/useLexicon';
import Loader from '../Loader';
import { canViewCurrentTasks, canViewClosedTasks, canViewFeedbackRequests, canViewSharedResponses, canViewFeedbackHistory, canAccessMarkingTools } from '../../utils/userAccessHelper';
import {
  filterTasksByProgressAndClass,
  getAwaitingSubmissions,
  getDraftTasks,
  getInProgressTasks,
  getTasksWithClasses,
  groupByClass,
} from '../../service/tasks';

const SecondSidebar = ({ id, setShowMenu }) => {
  const { data: profile, isLoadingdata: isLoadingProfile, resetData: resetProfile } = useProfile();
  const { t } = useLexicon();
  const { data: classData, isLoadingdata: isLoadingclassData } = useClassData();
  const [containerHeight, setContainerHeight] = useState(0);
  const [feedbackRequestsLength, setFeedbackRequestsLength] = useState(0);

  const { data: allAssignmentData, isLoadingdata: isAllAssignmentDataLoading } =
    useAssignmentsAll();
  const tasksWithClasses = getTasksWithClasses(allAssignmentData, classData);
  const { data: tasks, isLoadingdata: isLoadingTasks } = useTasks();
  const { data: tasksShares, resetData: resetTasksShares } = useShares('TASK');
  const { data: markingToolsShares, resetData: resetMarkingToolsShares } = useShares(null);
  const assignedTasks = filterTasksByProgressAndClass(tasks, 'ASSIGNED');
  const inProgressTasks = filterTasksByProgressAndClass(tasks, 'DRAFT');
  const inReviewTasks = filterTasksByProgressAndClass(tasks, 'REVIEW');
  const allStudentTasks =
    assignedTasks.length + inProgressTasks.length + inReviewTasks.length;

  const allTeacherTasks =
    getDraftTasks(allAssignmentData).length +
    getAwaitingSubmissions(allAssignmentData).length +
    getInProgressTasks(allAssignmentData).length;

  const location = useLocation();
  const history = useHistory();
  const role = getUserRole();
  const isTeacherNoClass = isTeacherWithoutClass(role, classData);
  const tabletView = isTabletView();

  const completedTasksQuery = useQuery({
    queryKey: ['completedTasks'],
    queryFn: async () => {
      const result = await getCompletedTasks();
      return result;
    },
  });

  const assignmentsQuery = useQuery({
    queryKey: ['assignments'],
    queryFn: async () => {
      const result = await getAssignments();
      return result;
    },
  });

  const communityTasksQuery = useQuery({
    queryKey: ['communityTasks'],
    queryFn: async () => {
      const result = await getCommunityTasks();
      return result;
    },
  });

  useEffect(() => {
    if (communityTasksQuery.data) {
      setFeedbackRequestsLength(communityTasksQuery.data?.totalElements);
    }
  }, [communityTasksQuery.data]);

  useEffect(() => {
    const updateHeight = () => {
      const windowHeight = window.innerHeight;
      setContainerHeight(`calc(${windowHeight}px - 170px)`);
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const handleButtonClick = async (link) => {
    if (link === '/sharedtasks') {
      try {
        await updateTasksViewedCount();
        resetProfile();
        resetTasksShares();
      } catch (error) {
        console.error('Error updating tasks viewed count:', error);
      }
    } else if (link === '/sharedmarkingtools') {
      try {
        await updateMarkingToolsViewedCount();
        await updateSuccessCriteriasViewedCount();
        await updateShortAnswersViewedCount();
        resetProfile();
        resetMarkingToolsShares();
      } catch (error) {
        console.error('Error updating marking tools viewed count:', error);
      }
    }
    
    if (setShowMenu) {
      setShowMenu(false);
    }
    history.push(link);
  };

  const closedTaskCount =
    getUserRole() === 'TEACHER'
      ? groupByClass(
          tasksWithClasses?.filter((task) => task.status === 'CLOSED')
        ).length
      : completedTasksQuery.data?.length;

  const countUnviewedShares = (shares, lastViewedTimestamp) => {
    if (!Array.isArray(shares)) return 0;
    if (!lastViewedTimestamp) return shares.length;
    
    const lastViewedDate = new Date(lastViewedTimestamp);
    return shares.filter((share) => {
      const resource = share.resource;
      const sharedOn = resource?.sharedOn;
      if (!sharedOn) return false;
      const sharedOnDate = new Date(sharedOn);
      return sharedOnDate > lastViewedDate;
    }).length;
  };

  const markingTemplateShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'MARKING_TEMPLATE';
  }) : [];

  const commentBankShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS';
  }) : [];

  const shortAnswerShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'SHORT_ANSWERS';
  }) : [];

  const successCriteriaShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'SUCCESS_CRITERIA';
  }) : [];

  const newSharedTasksCount = countUnviewedShares(Array.isArray(tasksShares) ? tasksShares : [], profile?.lastViewedTimestamps?.tasks);
  const newSharedMarkingToolsCount =
    countUnviewedShares(markingTemplateShares, profile?.lastViewedTimestamps?.markingCriterias) +
    countUnviewedShares(commentBankShares, profile?.lastViewedTimestamps?.commentbanks) +
    countUnviewedShares(successCriteriaShares, profile?.lastViewedTimestamps?.successCriterias) +
    countUnviewedShares(shortAnswerShares, profile?.lastViewedTimestamps?.shortAnswers) +
    countUnviewedShares(successCriteriaShares, profile?.lastViewedTimestamps?.successCriteria);

  const subLinks = [
    {
      icon: '',
      selectedIcon: '',
      title: `Current Tasks ${
        role === 'STUDENT' ? `(${allStudentTasks})` : `(${allTeacherTasks})`
      }`,
      link: '/',
      matchLink: '/',
    },
    {
      icon: '',
      selectedIcon: '',
      title: `Current Tasks ${
        role === 'STUDENT' ? `(${allStudentTasks})` : `(${allTeacherTasks})`
      }`,
      link: '/tasks',
      matchLink: '/tasks',
    },
    {
      icon: '',
      selectedIcon: '',
      title: `Closed Tasks (${closedTaskCount})`,
      link: `/completed`,
      matchLink: `/completed`,
    },
    {
      icon: '',
      selectedIcon: '',
      title: `${
        role === 'STUDENT'
          ? `Help a Friend (${feedbackRequestsLength})`
          : `Feedback Requests (${feedbackRequestsLength})`
      }`,
      link: '/giveFeedback',
      matchLink: '/giveFeedback',
    },
    {
      icon: '',
      selectedIcon: '',
      title: role === 'STUDENT' ? `Model Responses` : 'Exemplars',
      link: `/sharedresponses`,
      matchLink: `/sharedresponses`,
    },
    {
      icon: '',
      selectedIcon: '',
      title: `${role === 'STUDENT' ? `Feedback From Me` : `Feedback History`}`,
      link: `/feedbackHistory`,
      matchLink: `/feedbackHistory`,
    },
    {
      icon: markingTemplateUnSelected,
      selectedIcon: markingTemplateSelected,
      title: 'Rubrics',
      link: '/tools/rubrics',
      matchLink: '/tools/rubrics',
    },
    {
      icon: commentUnSelected,
      selectedIcon: commentSelected,
      title: 'Comment Banks',
      link: '/tools/commentbanks',
      matchLink: '/tools/commentbanks',
    },
    {
      icon: markUnSelected,
      selectedIcon: markSelected,
      title: 'Success Criteria',
      link: '/tools/successcriterias',
      matchLink: '/tools/successcriterias',
    },
    {
      icon: shortAnswerIcon,
      selectedIcon: purpleShortAnsIcon,
      title: 'Short Answers',
      link: '/tools/shortanswers',
      matchLink: '/tools/shortanswers',
    },
    {
      icon: '',
      selectedIcon: '',
      title: 'Shared With Me',
      link: `/sharedtasks`,
      matchLink: `/sharedtasks`,
      count: newSharedTasksCount,
    },
    {
      icon: markingTemplateUnSelected,
      selectedIcon: markingTemplateSelected,
      title: 'Rubrics',
      link: '/tools/rubrics',
      matchLink: id ? `/tools/rubrics/${id}` : '/tools/rubrics',
    },
    {
      icon: shareIcon,
      selectedIcon: shareIconSelected,
      title: 'Shared With Me',
      link: '/sharedmarkingtools',
      matchLink: '/sharedmarkingtools',
      count: newSharedMarkingToolsCount,
    },
    {
      icon: advancedControlsIcon,
      selectedIcon: advancedControlsActiveIcon,
      title: t('Marking Preferences'),
      link: '/tools/advanced-controls',
      matchLink: '/tools/advanced-controls',
    },
  ];

  const getSubLink = (link) => {
    return subLinks.find((subLink) => subLink?.link === link);
  };

  const markingToolsSubLinks = canAccessMarkingTools(profile)
    ? [
        getSubLink('/tools/rubrics'),
        getSubLink('/tools/commentbanks'),
        getSubLink('/tools/successcriterias'),
        getSubLink('/tools/shortanswers'),
        getSubLink('/tools/coursematerials'),
        getSubLink('/sharedmarkingtools'),
        getSubLink('/tools/advanced-controls'),
      ].filter(Boolean)
    : [];

  const subRoutes = isTeacherNoClass
    ? [
        {
          link: '/giveFeedback',
          subLinks: [
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewFeedbackHistory(profile) && subLinks[5],
          ].filter(Boolean),
        },
        {
          link: '/feedbackHistory',
          subLinks: [
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewFeedbackHistory(profile) && subLinks[5],
          ].filter(Boolean),
        },
        {
          link: '/',
          subLinks: [
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewFeedbackHistory(profile) && subLinks[5],
          ].filter(Boolean),
        },
        {
          link: '/sharedtasks',
          subLinks: [
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewFeedbackHistory(profile) && subLinks[5],
          ].filter(Boolean),
        },
        {
          link: '/settings',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics/:markingCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks/:commentBankId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/jeddai',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/sharedmarkingtools',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/coursematerials',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers/:shortAnswerId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias/:successCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/advanced-controls',
          subLinks: markingToolsSubLinks,
        },
      ]
    : [
        {
          link: '/',
          subLinks: [
            canViewCurrentTasks(profile) && subLinks[0],
            canViewClosedTasks(profile) && subLinks[2],
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewSharedResponses(profile) && subLinks[4],
            canViewFeedbackHistory(profile) && subLinks[5],
            subLinks[10],
          ].filter(Boolean),
        },
        {
          link: '/tasks',
          subLinks: [
            canViewCurrentTasks(profile) && subLinks[1],
            canViewClosedTasks(profile) && subLinks[2],
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewSharedResponses(profile) && subLinks[4],
            canViewFeedbackHistory(profile) && subLinks[5],
            subLinks[10],
          ].filter(Boolean),
        },
        {
          link: '/giveFeedback',
          subLinks: [
            canViewCurrentTasks(profile) && subLinks[1],
            canViewClosedTasks(profile) && subLinks[2],
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewSharedResponses(profile) && subLinks[4],
            canViewFeedbackHistory(profile) && subLinks[5],
            subLinks[10],
          ].filter(Boolean),
        },
        {
          link: '/feedbackHistory',
          subLinks: [
            canViewCurrentTasks(profile) && subLinks[1],
            canViewClosedTasks(profile) && subLinks[2],
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewSharedResponses(profile) && subLinks[4],
            canViewFeedbackHistory(profile) && subLinks[5],
            subLinks[10],
          ].filter(Boolean),
        },
        {
          link: '/sharedresponses',
          subLinks: [
            canViewCurrentTasks(profile) && subLinks[1],
            canViewClosedTasks(profile) && subLinks[2],
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewSharedResponses(profile) && subLinks[4],
            canViewFeedbackHistory(profile) && subLinks[5],
            subLinks[10],
          ].filter(Boolean),
        },
        {
          link: '/sharedtasks',
          subLinks: [
            canViewCurrentTasks(profile) && subLinks[1],
            canViewClosedTasks(profile) && subLinks[2],
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewSharedResponses(profile) && subLinks[4],
            canViewFeedbackHistory(profile) && subLinks[5],
            subLinks[10],
          ].filter(Boolean),
        },
        {
          link: '/settings',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics/:markingCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks/:commentBankId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias/:successCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers/:shortAnswerId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/jeddai',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/sharedmarkingtools',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/advanced-controls',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/completed',
          subLinks: [
            canViewCurrentTasks(profile) && subLinks[1],
            canViewClosedTasks(profile) && subLinks[2],
            canViewFeedbackRequests(profile) && subLinks[3],
            canViewSharedResponses(profile) && subLinks[4],
            canViewFeedbackHistory(profile) && subLinks[5],
            subLinks[10],
          ].filter(Boolean),
        },
      ];
  if (isLoadingclassData || isAllAssignmentDataLoading || isLoadingTasks || completedTasksQuery.isLoading || isLoadingProfile ) {
    return <Loader />;
  }

  return (
    <MainContainer height={containerHeight}>
      {subRoutes.map((route, idx) => {
        const isMatch = route.link === location.pathname || 
          (route.link.includes(':') && location.pathname.match(new RegExp(route.link.replace(/:[^/]+/g, '[^/]+'))));
        
        if (isMatch) {
          return (
            <React.Fragment key={idx}>
              {tabletView && setShowMenu && (
                <Button onClick={() => setShowMenu(false)}>
                  <CloseImg src={closeicon} />
                </Button>
              )}
              {route.subLinks.map((subLink, subIdx) => {
                    const isActive = isActiveButton(
                      subLink,
                      location.pathname,
                      isTeacherNoClass
                    );

                return (
                  <Button
                    key={subIdx}
                    onClick={() => handleButtonClick(subLink.link)}
                    active={isActive}
                  >
                    {subLink.icon && (
                      <IconImg
                        src={
                          location.pathname === subLink.matchLink ||
                          location.pathname.startsWith(`${subLink.matchLink}/`)
                            ? subLink.selectedIcon
                            : subLink.icon
                        }
                        alt={subLink.title}
                      />
                    )}
                    <span >{subLink.title}</span>
                    {subLink.count !== undefined && subLink.count > 0 && (
                      <CountBadge>{subLink.count}</CountBadge>
                    )}
                  </Button>
                );
              })}
            </React.Fragment>
          );
        } else {
          return null;
        }
      })}
    </MainContainer>
  );
};

export default SecondSidebar;
